import React, { ReactElement } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
    ChargePointImport,
    ConnectorImport,
    LoadBalancerGroup,
    Phase,
    useLoadBalancerGroupImportChargePointsV2Mutation,
} from '../../../common/redux/api/web-api'
import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import useText from '../../../common/hooks/useText'
import { ModalFlex, ModalSubtitle, ModalTitle } from '../../../ui/loadBalancer/LoadBalancer.styles'
import { isAuraImport } from '../../../lib/isAura'
import { isHaloImport } from '../../../lib/isHalo'
import { isDawnImport } from '../../../lib/isDawn'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { isLunaImport } from '../../../lib/isLuna'
import { EarthingSchemeLB } from '../../../common/enums/earthingSchemes'
import { PhaseMode, Phases } from '../../../common/enums/phaseTypes'

import LoadBalancerOfflineSettingsForm from './LoadBalancerOfflineSettingsForm'

type LBChargePointSettingsType = {
    chargePoints: Array<ChargePointImport>
    showModal: (show: boolean) => void
    group: LoadBalancerGroup
    updateChargePointList: () => void
}

export type OfflineSettings = {
    loadBalancerOfflineCurrent?: number | null
    loadBalancerOfflinePhase?: Phase | null
}

export default ({ chargePoints, showModal, group, updateChargePointList }: LBChargePointSettingsType): ReactElement => {
    const text = useText()
    const haloChargePoint = chargePoints.filter((chargePoint) => isHaloImport(chargePoint))
    const auraChargePoint = chargePoints.filter((chargePoint) => isAuraImport(chargePoint))
    const dawnChargePoint = chargePoints.filter((chargePoint) => isDawnImport(chargePoint))
    const lunaChargePoint = chargePoints.filter((chargePoint) => isLunaImport(chargePoint))
    const singleCharger = chargePoints?.length === 1

    //Get Min installation current for each charger type
    const minInstallationCurrentHalo = haloChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnector = nextChargePoint.connectors?.find(
                (connector) => connector.connectorId === 1 && connector.installationCurrent
            )

            if (currentConnector) {
                const currentInstallation = currentConnector?.installationCurrent
                if (!minCurrent || (currentInstallation && currentInstallation < minCurrent)) {
                    minCurrent = currentInstallation
                }
            }

            return minCurrent
        },
        undefined
    )

    const minInstallationCurrentAura = auraChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnectors = nextChargePoint.connectors

            if (currentConnectors) {
                currentConnectors.forEach((connector) => {
                    const currentInstallation: number | null | undefined = connector?.installationCurrent

                    if (
                        currentInstallation !== undefined &&
                        (!minCurrent || (currentInstallation && currentInstallation < minCurrent))
                    ) {
                        minCurrent = currentInstallation
                    }
                })
            }

            return minCurrent
        },
        undefined
    )

    const minInstallationCurrentDawn = dawnChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnector = nextChargePoint.connectors?.find(
                (connector) => connector.connectorId === 1 && connector.installationCurrent
            )

            if (currentConnector) {
                const currentInstallation = currentConnector?.installationCurrent
                if (!minCurrent || (currentInstallation && currentInstallation < minCurrent)) {
                    minCurrent = currentInstallation
                }
            }

            return minCurrent
        },
        undefined
    )

    const minInstallationCurrentLuna = lunaChargePoint?.reduce(
        (minCurrent: number | null | undefined, nextChargePoint) => {
            const currentConnector = nextChargePoint.connectors?.find(
                (connector) => connector.connectorId === 1 && connector.installationCurrent
            )

            if (currentConnector) {
                const currentInstallation = currentConnector?.installationCurrent
                if (!minCurrent || (currentInstallation && currentInstallation < minCurrent)) {
                    minCurrent = currentInstallation
                }
            }

            return minCurrent
        },
        undefined
    )

    /**********************************/

    /** IMPORT CHARGEPOINTS TO LB **/
    const [importChargePointsV2, { isLoading: importChargePointLoading }] =
        useLoadBalancerGroupImportChargePointsV2Mutation()

    const ocppChargePoints = chargePoints.find((chargePoint) => chargePoint?.ocppServerMode !== 'None')

    const earthingSystemBasedPhase = group?.earthingSystem === EarthingSchemeLB.IT ? Phases.L1L2 : Phases.L123

    const threePhasesOfflineSettings = (chargePoint: ChargePointImport, connector: ConnectorImport): Phase => {
        return chargePoint.ocppServerMode !== 'None' &&
            connector?.installationPhaseMode === (PhaseMode.threePhase || PhaseMode.twoPhase)
            ? earthingSystemBasedPhase
            : Phases.L1
    }

    const methods = useForm<Record<string, Phase | number | string | null>>({
        defaultValues: {
            lbc_1_halo:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,
            lbc_1_dawn:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,
            lbc_1_aura:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,

            lbc_2_aura:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[1]?.loadBalancerOfflineCurrent
                    : group?.minCurrentPerChargePoint,

            lbOfflinePhase_1:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[0]?.loadBalancerOfflinePhase
                    : Phases.L1,

            lbOfflinePhase_2:
                chargePoints?.length === 1 && chargePoints[0].connectors
                    ? chargePoints[0].connectors[1]?.loadBalancerOfflinePhase
                    : Phases.L1,
        },
        mode: 'all',
    })

    const connectorMappingOfflineCurrent = (connector: ConnectorImport): number | null | undefined => {
        if (connector.name === 'Socket' && connector.connectorId === 2) {
            return group?.minCurrentPerChargePoint
        }
        if (connector.name === 'Socket') {
            return methods.getValues().lbc_1_dawn as number
        }
        if (connector.name === 'Charger') {
            return methods.getValues().lbc_1_halo as number
        }
        if (connector.name === 'Left socket') {
            return methods.getValues().lbc_1_aura as number
        }
        if (connector.name === 'Right socket') {
            return methods.getValues().lbc_2_aura as number
        }

        return null
    }

    const connectorMappingOfflinePhase = (connector: ConnectorImport): Phase => {
        return connector?.connectorId === 1
            ? (methods.getValues().lbOfflinePhase_1 as Phase) ?? Phases.L1
            : (methods.getValues().lbOfflinePhase_2 as Phase) ?? Phases.L1
    }

    const doImportChargePointsV2 = async () => {
        const updatedChargePoints = chargePoints?.map((chargePoint: ChargePointImport) => ({
            ...chargePoint,
            hasError: false,
            errorReason: [],

            connectors: chargePoint.connectors?.map((connector) => ({
                ...connector,
                ...(connector && {
                    loadBalancerOfflinePhase:
                        chargePoints?.length === 1
                            ? connectorMappingOfflinePhase(connector)
                            : threePhasesOfflineSettings(chargePoint, connector),
                    loadBalancerOfflineCurrent:
                        connectorMappingOfflineCurrent(connector) ?? group?.minCurrentPerChargePoint,
                }),
            })),
        }))

        const importChargers = {
            isAutorotate: chargePoints.length > 1 && !ocppChargePoints,
            chargePoints: updatedChargePoints,
        }

        if (group?.id) {
            try {
                await importChargePointsV2({
                    id: group.id,
                    loadBalancerChargePointsImportModel: importChargers,
                }).unwrap()

                showModal(false)
                notifySuccess(text('lblLoadbalancerImportSuccess'))
                updateChargePointList()
            } catch (error) {
                showModal(false)
                notifyError(text('lblLoadbalancerImportFail'))
            }
        }
    }

    /************************************************/

    const isValid = methods.formState.isValid

    return (
        <BootstrapModalLayout
            title={text('lblLoadBalancerSettingsChargePoint')}
            actions={[
                {
                    disabled: !isValid,
                    label: text('btnSave'),
                    callback: methods.handleSubmit(doImportChargePointsV2),
                    loading: importChargePointLoading,
                },
                { label: text('btnCancel'), callback: () => showModal(false), btn: 'btn-secondary' },
            ]}
        >
            <FormProvider {...methods}>
                {haloChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <ModalFlex data-testid="halo-charger-info">
                                <ModalTitle>{haloChargePoint[0].id}</ModalTitle>
                                <ModalSubtitle>{haloChargePoint[0].name}</ModalSubtitle>
                            </ModalFlex>
                        )}
                        {chargePoints.length > 1 && (
                            <ModalFlex>
                                <ModalTitle className="md">{text('lblLoadBalancerSettingsChargePointHalo')}</ModalTitle>
                            </ModalFlex>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="halo"
                            singleCharger={singleCharger}
                            chargePoint={haloChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentHalo ?? 0}
                            earthingSystem={group?.earthingSystem}
                        />
                    </>
                )}
                {dawnChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <ModalFlex>
                                <ModalTitle>{dawnChargePoint[0].id}</ModalTitle>
                                <ModalSubtitle>{dawnChargePoint[0].name}</ModalSubtitle>
                            </ModalFlex>
                        )}
                        {chargePoints.length > 1 && (
                            <ModalFlex>
                                <ModalTitle className="md">{text('lblLoadBalancerSettingsChargePointDawn')}</ModalTitle>
                            </ModalFlex>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="dawn"
                            singleCharger={singleCharger}
                            chargePoint={dawnChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentDawn ?? 0}
                            earthingSystem={group?.earthingSystem}
                        />
                    </>
                )}

                {lunaChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <ModalFlex>
                                <ModalTitle>{lunaChargePoint[0].id}</ModalTitle>
                                <ModalSubtitle>{lunaChargePoint[0].name}</ModalSubtitle>
                            </ModalFlex>
                        )}
                        {chargePoints.length > 1 && (
                            <ModalFlex>
                                <ModalTitle className="md">{text('lblLoadBalancerSettingsChargePointLuna')}</ModalTitle>
                            </ModalFlex>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="luna"
                            singleCharger={singleCharger}
                            chargePoint={lunaChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentLuna ?? 0}
                            earthingSystem={group?.earthingSystem}
                        />
                    </>
                )}

                {auraChargePoint.length > 0 && (
                    <>
                        {chargePoints.length === 1 && (
                            <ModalFlex>
                                <ModalTitle>{auraChargePoint[0].id}</ModalTitle>
                                <ModalSubtitle>{auraChargePoint[0].name}</ModalSubtitle>
                            </ModalFlex>
                        )}
                        {chargePoints.length > 1 && (
                            <ModalFlex>
                                <ModalTitle className="md">{text('lblLoadBalancerSettingsChargePointAura')}</ModalTitle>
                            </ModalFlex>
                        )}

                        <LoadBalancerOfflineSettingsForm
                            chargerType="aura"
                            singleCharger={singleCharger}
                            chargePoint={auraChargePoint[0]}
                            minInstallationCurrent={minInstallationCurrentAura ?? 0}
                            earthingSystem={group?.earthingSystem}
                        />
                    </>
                )}
            </FormProvider>
        </BootstrapModalLayout>
    )
}
